import { Dispatch } from 'redux';
import TriggerController from '../controllers/TriggerController';
import commonHandler from '../errorHandlers/common-handler';
import { triggersHandler } from '../errorHandlers/triggers-handler';
import TriggerModel, {
  TriggerEventType,
  TriggerKind,
} from '../model/TriggerModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

export type CreateTriggerParams = {
  botId: number;
  sortOrder: number;
  active: boolean;
  randomize: boolean;
  kind?: TriggerKind;
  eventType?: TriggerEventType;
  phrases: { phrase: string; sortOrder: number }[];
  botResponses: {
    id?: number;
    pageId?: number | null;
    sortOrder: number;
    responseText?: string | null;
  }[];
};

export type RetrieveAllParams = {
  botId: number;
  kind?: string;
  page?: number;
  perPage?: number;
  orderField?: string;
  orderDirection?: string;
};

class TriggerCRUDActions extends CrudActionContainer {
  unsetTriggersAction: string;

  constructor() {
    super('TRIGGER');
    this.unsetTriggersAction = this.makeActionKey('UNSET_TRIGGERS');
  }

  create(chatbotId: number, entity: CreateTriggerParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const response = await TriggerController.postTrigger(entity);
        dispatch(this.retrieved(response));
        return response;
      } catch (err) {
        commonHandler(dispatch)(err);
        throw err;
      }
    };
  }

  retrieveById(triggerId: number) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<TriggerModel | undefined> => {
      try {
        return TriggerController.getById(triggerId);
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  retrieveAll(retrieveAllParams: RetrieveAllParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const { triggers, pagination } = await TriggerController.getAll(
          retrieveAllParams,
        );

        dispatch({
          type: this.retrievedAllAction,
          triggers,
          pagination,
        });
        return triggers;
      } catch (e) {
        await commonHandler(dispatch)(e);
      }
    };
  }

  update(entity: Partial<TriggerModel>) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));

        const response = await TriggerController.putTrigger(entity);
        dispatch(this.updateLocal(response));
        return response;
      } catch (e) {
        await triggersHandler(dispatch)(e, this.updateAction);
      }
    };
  }

  delete(entity: TriggerModel) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteLocalAction));

      await TriggerController.deleteTrigger(entity.id);
      dispatch(this.deleteLocal(entity));
    };
  }

  unsetTriggers() {
    return {
      type: this.unsetTriggersAction,
    };
  }
}

const triggerCRUDActions = new TriggerCRUDActions();

export default triggerCRUDActions;

export const useTriggerCRUDActions = getActionHookBuilder(triggerCRUDActions, [
  'retrieveAll',
  'retrieveById',
  'create',
  'update',
  'delete',
]);
